/**
 * td-search — header search dropdown (desktop) and full-screen takeover (mobile).
 *
 * One class, two instances differentiated by `data-td-search-variant="dropdown|takeover"`.
 * Default state is rendered server-side by snippets/td-search.liquid; the autocomplete
 * state is fetched from sections/td-predictive-search.liquid via the Section Rendering API
 * (the same mechanism the legacy predictive search uses) and injected client-side.
 *
 * Follows the TD vanilla-JS conventions: static selectors map with data-td-* hooks,
 * handlers bound in connectedCallback and torn down in disconnectedCallback.
 */
class TdSearch extends HTMLElement {
  static storageKey = 'tdRecentSearches';
  static maxRecent = 5;
  static debounceMs = 200;
  static minQueryLength = 2;

  static selectors = {
    form: '[data-td-search-form]',
    input: '[data-td-search-input]',
    clear: '[data-td-search-clear]',
    panel: '[data-td-search-panel]',
    defaultState: '[data-td-search-default]',
    results: '[data-td-search-results]',
    recent: '[data-td-search-recent]',
    recentList: '[data-td-search-recent-list]',
    close: '[data-td-search-close]',
  };

  connectedCallback() {
    this.variant = this.dataset.tdSearchVariant === 'takeover' ? 'takeover' : 'dropdown';
    // Live type-ahead is gated on the theme's `enable_autocomplete` setting.
    // When off, the curated default panel still opens, but typing does not fetch results.
    this.autocompleteEnabled = this.dataset.tdSearchAutocomplete !== 'false';

    this.form = this.querySelector(TdSearch.selectors.form);
    this.input = this.querySelector(TdSearch.selectors.input);
    this.clearButton = this.querySelector(TdSearch.selectors.clear);
    this.panel = this.querySelector(TdSearch.selectors.panel);
    this.defaultState = this.querySelector(TdSearch.selectors.defaultState);
    this.results = this.querySelector(TdSearch.selectors.results);
    this.recent = this.querySelector(TdSearch.selectors.recent);
    this.recentList = this.querySelector(TdSearch.selectors.recentList);
    this.closeButton = this.querySelector(TdSearch.selectors.close);

    if (!this.form || !this.input) return;

    // The sticky header (Headhesive) clones the entire nav wrapper — this component
    // included — and inserts the copy at the top of <body>, outside `.td-header`.
    // That produces a second live instance that shares our element ids, cross-wiring
    // the two `<label for>` / `aria-controls` targets and making the sticky field
    // focus and close erratically. Give every instance its own ids.
    this.scopeInstanceIds();

    this.isOpen = false;
    this.debounceTimer = null;
    this.abortController = null;
    this.selectedIndex = -1;
    this.previouslyFocused = null;

    // Bind handlers so they can be removed on disconnect.
    this.onInput = this.onInput.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.form.addEventListener('submit', this.onSubmit);
    if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
    if (this.closeButton) this.closeButton.addEventListener('click', this.onClose);
    document.addEventListener('keydown', this.onKeydown);

    if (this.variant === 'dropdown') {
      this.input.addEventListener('focus', this.onFocus);
      document.addEventListener('click', this.onDocumentClick);
    } else {
      // Takeover is opened by external trigger(s) sitting in the mobile header.
      this.triggers = Array.from(document.querySelectorAll('[data-td-search-open]'));
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onTriggerClick));
    }

    this.renderRecent();
    this.syncClearVisibility();
    // TD analytics hook: component mounted (fire `search_opened` when actually opened below).
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('focus', this.onFocus);
    }
    if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
    if (this.closeButton) this.closeButton.removeEventListener('click', this.onClose);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    if (this.triggers) {
      this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.onTriggerClick));
    }
    this.clearDebounce();
    this.abortInFlight();
    this.unlockScroll();
  }

  /* ---------- open / close ---------- */

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    this.renderRecent(); // refresh from localStorage each time the panel opens

    // Freeze the page while the search is open so the panel stays put.
    this.lockScroll();

    if (this.variant === 'takeover') {
      this.previouslyFocused = document.activeElement;
      // Defer focus so the takeover is painted before the keyboard is summoned on iOS.
      window.requestAnimationFrame(() => this.input.focus());
    }
    // TD analytics hook: search_opened
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resetSelection();
    this.unlockScroll();

    if (this.variant === 'takeover') {
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
    }
  }

  /**
   * Re-scope this instance's element ids so a cloned copy (see connectedCallback)
   * can't share ids with the original. Rewrites the input id plus the `<label for>`
   * and `aria-controls` that point at it, and the panel id.
   */
  scopeInstanceIds() {
    if (!this.input || !this.panel) return;
    TdSearch.instanceCount = (TdSearch.instanceCount || 0) + 1;
    const uid = `${this.variant}-${TdSearch.instanceCount}`;
    const inputId = `td-search-input-${uid}`;
    const panelId = `td-search-panel-${uid}`;
    const label = this.input.id ? this.querySelector(`label[for="${CSS.escape(this.input.id)}"]`) : null;
    this.input.id = inputId;
    if (label) label.setAttribute('for', inputId);
    this.panel.id = panelId;
    this.input.setAttribute('aria-controls', panelId);
  }

  onFocus() {
    this.open();
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.open();
  }

  onClose(event) {
    if (event) event.preventDefault();
    this.close();
  }

  onDocumentClick(event) {
    if (!this.isOpen) return;
    if (this.contains(event.target)) return;
    this.close();
  }

  /* ---------- query handling ---------- */

  onInput() {
    this.syncClearVisibility();
    // With autocomplete disabled, keep the default panel and skip live results.
    if (!this.autocompleteEnabled) return;

    const term = this.input.value.trim();
    this.clearDebounce();

    if (term.length < TdSearch.minQueryLength) {
      this.showDefaultState();
      return;
    }

    // TD analytics hook: query_typed { term }
    this.debounceTimer = window.setTimeout(() => this.fetchResults(term), TdSearch.debounceMs);
  }

  onClear(event) {
    if (event) event.preventDefault();
    this.input.value = '';
    this.syncClearVisibility();
    this.showDefaultState();
    this.input.focus();
  }

  showDefaultState() {
    this.abortInFlight();
    this.resetSelection();
    if (this.results) {
      this.results.innerHTML = '';
      this.results.hidden = true;
    }
    if (this.defaultState) this.defaultState.hidden = false;
  }

  showResultsState() {
    if (this.defaultState) this.defaultState.hidden = true;
    if (this.results) this.results.hidden = false;
  }

  async fetchResults(term) {
    if (!this.results) return;
    this.abortInFlight();
    this.abortController = new AbortController();

    const base = (window.PXUTheme && window.PXUTheme.routes && window.PXUTheme.routes.predictive_search_url) || '/search/suggest';
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'query,collection,product',
      section_id: 'td-predictive-search',
    });
    const url = `${base}?${params.toString()}`;

    this.setAttribute('data-loading', 'true');

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-td-predictive-search');

      // Trust boundary: `section` is same-origin HTML rendered by Shopify's Section
      // Rendering API (sections/td-predictive-search.liquid) — not user-supplied — so
      // assigning innerHTML here is safe (same pattern as the vendor predictive search).
      this.results.innerHTML = section ? section.innerHTML : '';
      this.resetSelection();
      this.showResultsState();

      // TD analytics hook: zero_results_shown { term } when [data-td-search-empty] is present.
    } catch (error) {
      if (error.name === 'AbortError') return;
      // On failure, fall back to the default state rather than showing a broken panel.
      this.showDefaultState();
    } finally {
      this.removeAttribute('data-loading');
    }
  }

  /* ---------- submit + recent searches ---------- */

  onSubmit(event) {
    const term = this.input.value.trim();
    if (!term) {
      event.preventDefault();
      return;
    }
    this.storeRecent(term);
    // TD analytics hook: query_submitted { term }
    // Native submit proceeds to routes.search_url.
  }

  getRecent() {
    try {
      const raw = window.localStorage.getItem(TdSearch.storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((term) => typeof term === 'string') : [];
    } catch (_error) {
      return [];
    }
  }

  storeRecent(term) {
    try {
      const existing = this.getRecent().filter((item) => item.toLowerCase() !== term.toLowerCase());
      const next = [term, ...existing].slice(0, TdSearch.maxRecent);
      window.localStorage.setItem(TdSearch.storageKey, JSON.stringify(next));
    } catch (_error) {
      /* localStorage can fail in private browsing or restricted environments */
    }
  }

  renderRecent() {
    if (!this.recent || !this.recentList) return;
    const terms = this.getRecent();

    if (terms.length === 0) {
      this.recent.hidden = true;
      this.recentList.innerHTML = '';
      return;
    }

    const searchUrl = (window.PXUTheme && window.PXUTheme.routes && window.PXUTheme.routes.search_url) || '/search';
    this.recentList.innerHTML = terms
      .map((term) => {
        const safe = this.escapeHtml(term);
        const href = `${searchUrl}?q=${encodeURIComponent(term)}`;
        return `<a class="td-search__recent-link" href="${href}">${safe}</a>`;
      })
      .join('');
    this.recent.hidden = false;
  }

  /* ---------- keyboard navigation ---------- */

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (this.variant === 'takeover' && event.key === 'Tab') {
      this.trapFocus(event);
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (this.results && this.results.hidden) return;

    const options = this.getResultOptions();
    if (options.length === 0) return;

    event.preventDefault();
    if (event.key === 'ArrowDown') {
      this.selectedIndex = (this.selectedIndex + 1) % options.length;
    } else {
      this.selectedIndex = (this.selectedIndex - 1 + options.length) % options.length;
    }
    this.highlightOption(options);
  }

  getResultOptions() {
    if (!this.results || this.results.hidden) return [];
    return Array.from(this.results.querySelectorAll('[data-td-search-option]'));
  }

  highlightOption(options) {
    options.forEach((option, index) => {
      const selected = index === this.selectedIndex;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) {
        option.focus({ preventScroll: false });
      }
    });
  }

  resetSelection() {
    this.selectedIndex = -1;
    if (!this.results) return;
    this.results
      .querySelectorAll('[data-td-search-option][aria-selected="true"]')
      .forEach((option) => option.setAttribute('aria-selected', 'false'));
  }

  trapFocus(event) {
    const focusable = this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- helpers ---------- */

  syncClearVisibility() {
    if (!this.clearButton) return;
    this.clearButton.hidden = this.input.value.trim().length === 0;
  }

  clearDebounce() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  abortInFlight() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  lockScroll() {
    // Respect a lock another component (menu drawer, mini-cart) already holds:
    // don't take ownership, so we won't release theirs on close.
    if (document.body.classList.contains('scroll-locked')) return;
    // Pad the body by the now-missing scrollbar width so the fixed header and page
    // don't shift right when `overflow: hidden` removes the desktop scrollbar.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      this.previousBodyPaddingRight = document.body.style.paddingRight;
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add('scroll-locked');
    this.scrollLockedByUs = true;
  }

  unlockScroll() {
    if (!this.scrollLockedByUs) return;
    document.body.classList.remove('scroll-locked');
    document.body.style.paddingRight = this.previousBodyPaddingRight || '';
    this.scrollLockedByUs = false;
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('td-search')) {
  customElements.define('td-search', TdSearch);
}
